import { Clock3, ListOrdered } from "lucide-react"

import type { Recipe } from "@/types/recipe"

type RecipeStepsProps = {
  recipe: Recipe
}

export function RecipeSteps({ recipe }: RecipeStepsProps) {
  const stepsCount = recipe.steps.length

  return (
    <section
      id="steps"
      className="rounded-[2rem] border border-neutral-200 bg-white p-6 shadow-sm sm:p-8"
    >
      <div className="mb-6">
        <div className="eyebrow">Покроково</div>

        <h2 className="mt-4 text-2xl font-semibold text-neutral-950 sm:text-3xl">
          Як приготувати
        </h2>

        <p className="mt-3 text-sm leading-7 text-neutral-600 sm:text-base">
          Рецепт розбитий на зрозумілі кроки, щоб ним було зручно користуватися
          прямо на кухні. Рухайтеся послідовно — так простіше контролювати
          процес і не пропустити важливі дрібниці.
        </p>
      </div>

      <div className="mb-5 rounded-[1.5rem] border border-neutral-200 bg-neutral-50 p-4">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-800">
              <ListOrdered className="h-5 w-5" />
            </div>

            <div>
              <div className="text-sm font-semibold text-neutral-950">
                Маршрут приготування
              </div>
              <p className="mt-1 text-sm leading-7 text-neutral-600">
                У рецепті {stepsCount}{" "}
                {stepsCount === 1
                  ? "крок"
                  : stepsCount >= 2 && stepsCount <= 4
                  ? "кроки"
                  : "кроків"}
                . Найзручніше пройти їх по черзі, не перескакуючи між етапами.
              </p>
            </div>
          </div>

          <div className="inline-flex items-center rounded-full border border-neutral-200 bg-white px-3 py-1 text-xs font-semibold text-neutral-700">
            <Clock3 className="mr-2 h-3.5 w-3.5 text-emerald-700" />
            Час рецепта: {recipe.totalTimeMinutes} хв
          </div>
        </div>
      </div>

      <div className="space-y-4">
        {recipe.steps.map((step, index) => (
          <div
            id={`step-${index + 1}`}
            key={`${recipe.slug}-step-${index + 1}`}
            className="scroll-mt-24 rounded-[1.5rem] border border-neutral-200 bg-white p-5"
          >
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-emerald-100 text-sm font-semibold text-emerald-900">
                {index + 1}
              </div>

              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-neutral-950">
                  {step.title ?? `Крок ${index + 1}`}
                </h3>

                <p className="mt-2 text-sm leading-7 text-neutral-600 sm:text-base">
                  {step.description}
                </p>

                {typeof step.timerMinutes === "number" ? (
                  <div className="mt-3 inline-flex rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-800">
                    Орієнтир по часу: {step.timerMinutes} хв
                  </div>
                ) : null}

                {step.tip ? (
                  <div className="mt-4 rounded-[1.25rem] border border-emerald-200 bg-emerald-50/70 p-4">
                    <div className="text-sm font-semibold text-neutral-950">
                      Маленька підказка
                    </div>
                    <p className="mt-1 text-sm leading-7 text-neutral-700">
                      {step.tip}
                    </p>
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}